<template>
  <div v-if="displayConcierge">
    <slot />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { trackEventConstant } from "../../plugins/analytics.constant";
import { storeAction } from '../../store/storeActions.constant';
import { storeGetters } from "@/store/storeGetters.constant";
import StatusOverlay from "@/components/StatusOverlay/StatusOverlay";
import { ypoConciergeConstant } from "./ypoConcierge.constant";
import IconYpoConcierge from "../Icons/IconYpoConcierge";
import YpoConciergeLayout from "@/components/YpoConcierge/YpoConciergeLayout.vue";

export default {
  name: 'YpoConciergeWrapper',
  components: { YpoConciergeLayout, StatusOverlay, IconYpoConcierge },
  data() {
    return {
      ypoConciergeConstant,
      trackEventConstant,
      ypoConciergeHelpInitiated: false,
      chatInitializationInProgress: false,
      buttonConfig: {
        direction: 'top',
        transition: 'slide-y-reverse-transition',
      },
      requestAssistanceLink: process.env.VUE_APP_YPO_SF_ASSISTANCE_URL || "",
    };
  },
  created() {
    if (this.determineConciergeServiceAvailability) this.determineConciergeServiceAvailability();
  },
  methods: {
    ...mapActions({
      determineConciergeServiceAvailability: `ypoConciergeService/${storeAction.determineConciergeServiceAvailability}`,
    }),
  },
  computed: {
    ...mapState('actionState', ['actions']),
    ...mapGetters({
      isYpoConciergeAgentAvailable: `ypoConciergeService/${storeGetters.isYpoConciergeAgentAvailable}`,
      conciergeScriptLoaded: `ypoConciergeService/${storeGetters.isConciergeScriptLoaded}`,
      isConciergeActive: `ypoConciergeService/${storeGetters.isConciergeActive}`,
      profileInformation: `myAccount/${storeGetters.profileInformation}`,
      routeConfigHideConcierge: storeGetters.routeConfigHideConcierge,
    }),
    displayConcierge() {
      return !this.routeConfigHideConcierge && this.profileInformation && this.profileInformation.recordTypeName
          && ypoConciergeConstant.rolesWithAccessToConcierge.includes(this.profileInformation.recordTypeName);
    },
  },
};
</script>

<style lang="scss">
@import '../../common/styles/_colors.scss';
@import '../../common/styles/_commonClasses.scss';

.embeddedServiceSidebar.modalContainer {
  @include breakpointMin(md) {
    z-index: 12!important;
  }
}
.embeddedServiceSidebar.layout-docked .dockableContainer {
  background-color: $white;
  font-family: "Noto Sans", sans-serif !important;
  max-width: 350px !important;
  max-height: 400px !important;
  bottom: 90px !important;
  position: fixed !important;
  right: 0 !important;

  @include breakpoint(sm) {
    right: 16px !important;
    left: auto !important;
  }

  @include breakpointMin(md) {
    bottom: 12% !important;
    max-width: 300px !important;
  }

  @include breakpoint(xs) {
    max-height: 500px;
    width: 95%;
  }

  .form-element__help {
    color: $ypo-error-red;
    padding-left: 5px;
  }

  .slds-dropdown {
    max-height: 200px;
    width: 100%;
    overflow: auto;
    @extend %inner-scrollbar;
  }

  .sidebarHeader {
    background-color: $ypo-blue;
  }

  .waitingCancelChat {
    text-decoration: none;
    width: 200px;

    &:hover {
      text-decoration: underline;
    }
  }

  .embeddedServiceSidebarButton {
    background-color: $ypo-blue;
    color: $white !important;
    text-transform: uppercase;
    font-family: "Noto Sans", sans-serif !important;
  }

  .dialogButton {
    background-color: $ypo-blue;
    color: $white;
    text-transform: uppercase;
    text-decoration: none;

    &:hover {
      background-color: $ypo-blue;
      text-decoration: underline;
    }
  }

  .uiButton--inverse {
    color: $ypo-blue;
    background-color: $white;

    &:hover {
      background-color: $ypo-light-medium-gray;
      text-decoration: none;
    }
  }

  .endChatButton {
    background-color: $ypo-blue;
    color: $white !important;
    text-transform: uppercase;
    text-decoration: none;
    margin-bottom: 5px;

    &:hover {
      background-color: $ypo-blue;
      text-decoration: underline;
    }

    .label {
      color: $white !important;
    }
  }

  .prechatUI {
    padding-top: 20px;
    height: 100%;
    position: relative;
  }

  .slds-var-p-around_medium {
    padding: 10px;
  }

  .uiLabel {
    font-size: 13px;
    color: $ypo-blue;

    .required {
      display: none;
    }
  }
}

.embeddedServiceSidebar {
  font-family: "Noto Sans", sans-serif !important;

  .saveTranscriptButton {
    display: none;
    @include breakpointMin(md) {
      display: inline-block;
    }
  }

  .footer-menu {
    height: 67px !important;
    width: 40px !important;
    display: none;
    @include breakpointMin(md) {
      display: block;
    }

    .slds-dropdown {
      width: 280px !important;
    }

    .footer-menu-items {
      width: 100% !important;
    }

    .slds-dropdown-trigger_click {
      padding: 5px 0 0 0 !important;
    }
  }

  .chasitorControls {
    .Send {
      padding-left: 10px !important;
    }
  }

  @include breakpoint(sm) {
    padding: 20px 5px;
  }

  .minimizedContainer {
    background-color: $ypo-blue !important;
    bottom: 60px !important;
  }

  .agentInitial {
    background-color: $ypo-blue !important;
  }

  .chasitor {
    .plaintextContent {
      background-color: $ypo-blue !important;
    }
  }
}

.slds-icon {
  height: 20px;
  width: 20px;
}

.slds-icon_xx-small, .slds-icon--xx-small {
  width: 14px;
  height: 14px;
  line-height: 16px;
}

.slds-icon-text-default {
  fill: $ypo-dim-gray;
}

.startButtonWrapper {
  padding-top: 15px;
  position: absolute;
  width: 100%;
  padding-right: 20px;
  bottom: 20px;
}

.startButton {
  background: $ypo-blue !important;
  color: $white !important;
  border-radius: 4px;
  padding: 8px 28px;
  min-width: inherit;
  height: auto;
  font-size: 14px;
  font-weight: 600;
  box-shadow: none !important;

  &:hover {
    background: $ypo-blue !important;
    color: $white !important;
  }

  &:disabled {
    background-color: $ypo-medium-gray !important;
    color: $white !important;
    pointer-events: none;
    cursor: no-drop;
  }
}

.slds-media[aria-checked="true"] {
  background-color: $ypo-blue !important;
  color: $white !important;
}

.slds-listbox__option-icon {
  display: none !important;
}

abbr[title] {
  border-bottom: none !important;
  text-decoration: none;
}

.slds-form-element {
  &__label {
    color: $ypo-black-opacity;
    font-size: 12px;
    padding-right: 8px;
    padding-top: 4px;
    font-weight: 600;
    margin-bottom: 2px;
  }

  &__help {
    font-size: 12px;
    color: $ypo-error-red !important;
    margin-bottom: 5px;
  }

  &__control {
    clear: left;
    position: relative;
  }
}

.cSFP_PreChatForm .topicContainer {
  text-align: left;
}

.slds-combobox {
  position: static;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;

  &_container {
    display: flex;
    flex-direction: column;
    position: relative;
  }

  &__form-element {
    display: flex;
    flex: 1 1 auto;
    margin-bottom: 5px;
    margin-top: 5px;
  }

  &__input {
    font-size: 12px;
  }

  .slds-input-has-icon {
    position: relative;
  }

  .slds-combobox_container.slds-is-open .slds-dropdown {
    display: block
  }

  .slds-combobox_container.slds-has-selection .slds-combobox__input-value, .slds-combobox_container.slds-has-selection .slds-combobox__input-value:focus {
    box-shadow: 0 0 0 2px $white inset, 0 0 0 3px $ypo-silver inset
  }

  .slds-combobox_container.slds-has-icon-only .slds-combobox__input, .slds-combobox_container.slds-has-icon-only .slds-combobox__input:focus, .slds-combobox_container.slds-has-icon-only .slds-combobox__input.slds-has-focus {
    width: 0;
    pointer-events: auto
  }

  .slds-combobox_container.slds-has-icon-only .slds-input__icon {
    z-index: 2;
    pointer-events: none
  }

  [role=combobox] input[readonly], input[readonly][role=combobox] {
    padding-left: 12px;
    border-color: $ypo-silver;
    background-color: $white;
    font-size: 12px;
    font-weight: 400;
  }

  [role=combobox] input[readonly]:focus, [role=combobox] input[readonly].slds-has-focus, input[readonly][role=combobox]:focus, input[readonly][role=combobox].slds-has-focus {
    border-color: $ypo-cta-secondary;
    box-shadow: 0 0 3px $ypo-cta;
  }

  [role=combobox] input[readonly][disabled], input[readonly][role=combobox][disabled] {
    background-color: $ypo-light-gray-background;
    border-color: $ypo-about-border;
  }

  .slds-input {
    padding: 0 16px 0 12px;
    width: 100%;
    min-height: calc(30px + (1px * 2));
    line-height: 30px;
    border: 1px solid $ypo-silver;
    border-radius: 2px;
    background-color: $white;
    color: $ypo-dim-gray;
    transition: border 0.1s linear, background-color 0.1s linear;
    display: inline-block
  }

  .slds-input-has-icon_right .slds-input {
    padding-right: 32px;
  }

  .slds-input__icon-group_right {
    right: 20px;
    top: 50%;
  }

  .slds-input__icon-group {
    position: absolute;
    height: 16px;
    margin-top: -8px;
  }

  .slds-input-has-icon .slds-input__icon:not(button) {
    pointer-events: none;
  }

  .slds-icon_container, .slds-icon__container {
    display: inline-block;
    border-radius: 2px;
    line-height: 1;
  }

  .slds-input-has-icon .slds-input__icon {
    width: 0.875rem;
    height: 0.875rem;
    position: absolute;
    top: 50%;
    margin-top: -.4375rem;
    line-height: 1;
    border: 0;
    z-index: 2;
  }

  .slds-has-error .slds-input__icon {
    fill: $ypo-error-red;
    color: $ypo-error-red;
  }

  .slds-input:focus, .slds-input:active {
    border-color: $ypo-cta-secondary;
    background-color: $white;
    box-shadow: 0 0 3px $ypo-cta $ypo-cta;
    outline: 0
  }

  .slds-dropdown-trigger {
    position: relative;
    display: inline-block;
  }

  .slds-listbox {
    &__option {
      :hover {
        cursor: pointer;
        text-decoration: none;
      }

      :focus {
        outline: 0;
        text-decoration: none;
      }
    }

    &__option-icon {
      width: 24px;
      display: inline-flex;
      align-content: center;
      align-items: center;
      justify-content: center;
      color: $ypo-dim-gray;
    }

    .slds-listbox_vertical {
      padding: 8px 12px;

    }

    .slds-has-focus {
      background: $ypo-blue;
      color: $white;
    }

    &__option_plain {
      align-items: center;
      display: flex;
      padding: 8px 12px;
    }

    &__option-icon {
      width: 24px;
      display: inline-flex;
      align-content: center;
      align-items: center;
      justify-content: center;
      color: $ypo-dim-gray;
    }

    .slds-media_center, .slds-media--center {
      align-items: center;
    }

    .slds-truncate {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .slds-media {
      display: flex;

      &__body {
        font-size: 12px;
        flex: 1;
        min-width: 0;
        margin-bottom: 0;
      }
    }
  }
}

.slds-dropdown-trigger .slds-dropdown {
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.1s linear, visibility 0.1s linear
}

.slds-dropdown-trigger:hover, .slds-dropdown-trigger:focus {
  outline: 0
}

.slds-dropdown-trigger:hover .slds-dropdown, .slds-dropdown-trigger:focus .slds-dropdown {
  visibility: visible;
  opacity: 1;
  transition: opacity 0.1s linear, visibility 0.1s linear
}

.slds-dropdown-trigger {
  position: relative;
  display: inline-block
}

.slds-dropdown-trigger .slds-dropdown {
  top: 100%
}

.slds-dropdown-trigger .slds-dropdown_bottom, .slds-dropdown-trigger .slds-dropdown--bottom {
  top: auto
}

.slds-dropdown-trigger_hover .slds-dropdown, .slds-dropdown-trigger--hover .slds-dropdown {
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.1s linear, visibility 0.1s linear
}

.slds-dropdown-trigger_hover:hover, .slds-dropdown-trigger_hover:focus, .slds-dropdown-trigger--hover:hover, .slds-dropdown-trigger--hover:focus {
  outline: 0
}

.slds-dropdown-trigger_hover:hover .slds-dropdown, .slds-dropdown-trigger_hover:focus .slds-dropdown, .slds-dropdown-trigger--hover:hover .slds-dropdown, .slds-dropdown-trigger--hover:focus .slds-dropdown {
  visibility: visible;
  opacity: 1;
  transition: opacity 0.1s linear, visibility 0.1s linear
}

.slds-dropdown-trigger_click .slds-dropdown, .slds-dropdown-trigger_click:hover .slds-dropdown, .slds-dropdown-trigger--click .slds-dropdown, .slds-dropdown-trigger--click:hover .slds-dropdown {
  display: none
}

.slds-dropdown-trigger_click.slds-is-open .slds-dropdown, .slds-dropdown-trigger--click.slds-is-open .slds-dropdown {
  display: block;
  visibility: visible;
  opacity: 1
}

.slds-dropdown-trigger > [class*=slds-button_icon] ~ .slds-dropdown_left[class*=slds-nubbin], .slds-dropdown-trigger > [class*=slds-button_icon] ~ .slds-dropdown--left[class*=slds-nubbin], .slds-dropdown-trigger > [class*=slds-button--icon] ~ .slds-dropdown_left[class*=slds-nubbin], .slds-dropdown-trigger > [class*=slds-button--icon] ~ .slds-dropdown--left[class*=slds-nubbin] {
  left: calc(-1 * 0.5rem)
}

.slds-dropdown-trigger > [class*=slds-button_icon] ~ .slds-dropdown_right[class*=slds-nubbin], .slds-dropdown-trigger > [class*=slds-button_icon] ~ .slds-dropdown--right[class*=slds-nubbin], .slds-dropdown-trigger > [class*=slds-button--icon] ~ .slds-dropdown_right[class*=slds-nubbin], .slds-dropdown-trigger > [class*=slds-button--icon] ~ .slds-dropdown--right[class*=slds-nubbin] {
  right: calc(-1 * 0.5rem)
}

.slds-dropdown_left, .slds-dropdown--left {
  left: 0;
  right: auto;
  transform: translateX(0);
}

.slds-dropdown_fluid, .slds-dropdown--fluid {
  min-width: 192px;
  max-width: 100%;
  width: 100%;
}

.slds-dropdown {
  position: absolute;
  z-index: 7000;
  left: 50%;
  float: left;
  min-width: 96px;
  max-width: 320px;
  margin-bottom: 2px;
  border: 1px solid $ypo-silver;
  border-radius: 4px;
  padding: 5px 0;
  font-size: 12px;
  background: $white;
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.16);
  transform: translateX(-50%);
  color: $ypo-black-opacity;
}

</style>
